import { useEffect, useState } from 'react';
import { EquipmentItem } from '@/backend/models/equipmentItem';
import { nativeBridge } from '@/frontend/embed/nativeBridge';
import { DESKTOP_INSTRUMENT_TYPE, DesktopInstrument, ProviderConfigSchema } from '@/types/instrumentBridgeTypes';

/**
 * The configuration fields a connection takes, and their current values.
 *
 * Ported from veriform-desktop's `ConnectionConfig` (Equipment/
 * ConnectionConfig.tsx:20), whose effect decides each field's starting value.
 * The order there is: whatever the connection was last given, else the
 * equipment item's default for that key, else blank. The one subtlety is the
 * enum check -- a default that is not one of a field's allowed values is
 * dropped rather than offered as a selection the provider would reject.
 *
 * The field list itself is the provider's: only the engine knows it, so nothing
 * can be shown until it answers. Manual control has no provider, so the engine
 * is not asked.
 */

const EMPTY: ProviderConfigSchema = {
  connectionConfig: {},
  fullConnectionSchema: {},
  requiredFields: [],
};

export interface ConnectionConfigState {
  loading: boolean;
  fields: Record<string, string>;
  schema: ProviderConfigSchema;
  /** True once it is known the provider has no fields to configure. */
  empty: boolean;
  /** Set when the engine could not describe the provider -- not the same as no fields. */
  error?: string;
  setField: (key: string, value: string) => void;
  isRequired: (key: string) => boolean;
}

export function useConnectionConfig(
  equipment: EquipmentItem,
  instrument: DesktopInstrument | undefined,
  storedConfiguration: string | undefined,
): ConnectionConfigState {
  const [schema, setSchema] = useState<ProviderConfigSchema>(EMPTY);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [error, setError] = useState<string>();
  const hasProvider = Boolean(instrument) && instrument?.instrumentType !== DESKTOP_INSTRUMENT_TYPE.MANUAL_CONTROL;
  // Loading from the first render when there is an instrument to ask about.
  // Starting at false would report "nothing to configure" for one render before
  // the question has been asked, and a caller acting on `empty` -- closing the
  // dialog, say -- would act on an answer that had not come back yet.
  const [loading, setLoading] = useState(hasProvider);

  const instrumentId = hasProvider ? instrument?.id : undefined;
  const defaults = equipment.defaultConfiguration?.objectInstance?.properties ?? [];

  useEffect(() => {
    setError(undefined);
    if (!instrumentId) {
      setSchema(EMPTY);
      setFields({});
      setLoading(false);
      return () => {};
    }

    let cancelled = false;
    setLoading(true);

    nativeBridge
      .call<ProviderConfigSchema>('engine.providerConfigSchema', { instrumentId })
      .then(reply => {
        if (cancelled) return;
        // Guarded rather than trusted: this crosses the bridge from another
        // app, and a reply that is not the shape expected should leave the
        // dialog empty rather than fail the render it sits in.
        const schemaReply: ProviderConfigSchema = {
          connectionConfig: reply?.connectionConfig ?? {},
          fullConnectionSchema: reply?.fullConnectionSchema ?? {},
          requiredFields: reply?.requiredFields ?? [],
        };
        setSchema(schemaReply);
        setFields(startingValues(schemaReply, defaults, storedConfiguration));
      })
      // Shown in the dialog rather than read as "no fields": that would record
      // the choice and close, as desktop never does when its schema call fails.
      .catch((reason: Error) => {
        if (cancelled) return;
        setSchema(EMPTY);
        setError(reason?.message ?? String(reason));
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
    // Keyed on the equipment item rather than `defaults`, which is rebuilt
    // every render and would re-ask the engine each time.
  }, [instrumentId, equipment.id, storedConfiguration]);

  return {
    loading,
    fields,
    schema,
    empty: !loading && !error && Object.keys(schema.connectionConfig).length === 0,
    error,
    setField: (key: string, value: string) => setFields(current => ({ ...current, [key]: value })),
    isRequired: (key: string) => schema.requiredFields.includes(key),
  };
}

function startingValues(
  schema: ProviderConfigSchema,
  defaults: { key: string; value: string }[],
  storedConfiguration: string | undefined,
): Record<string, string> {
  const stored = parseConfiguration(storedConfiguration);

  return Object.fromEntries(
    Object.keys(schema.connectionConfig).map(key => {
      const fallback = defaults.find(property => property.key === key)?.value;
      const allowed = schema.fullConnectionSchema[key]?.enum;

      // An equipment default outside the provider's allowed values is no use:
      // offering it would only have the provider reject the connection.
      if (!stored[key] && allowed && !allowed.includes(fallback as string)) {
        return [key, ''];
      }
      return [key, stored[key] || fallback || ''];
    }),
  );
}

function parseConfiguration(configuration: string | undefined): Record<string, string> {
  if (!configuration) return {};
  try {
    return JSON.parse(configuration);
  } catch {
    return {};
  }
}
